import { useToast } from "@/components/Toast";
import { api } from "@/lib/api";
import { relativeTime } from "@/lib/utils";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Loader2, MessageSquare, Send } from "lucide-react";
import { useState } from "react";

// ============================================================
// Booking Detail — Notes Tab (Notizen anzeigen + erstellen)
// ============================================================

interface NoteEntry {
	id: string;
	field: string;
	oldValue: string | null;
	newValue: string | null;
	source: string;
	createdAt: string;
}

export function NotesTab({ bookingId }: { bookingId: string }) {
	const { toast } = useToast();
	const queryClient = useQueryClient();
	const [content, setContent] = useState("");

	const query = useQuery({
		queryKey: ["booking-notes", bookingId],
		queryFn: () =>
			api.get<{ data: NoteEntry[] }>(`/bookings/${bookingId}/notes`),
	});

	const addNote = useMutation({
		mutationFn: (text: string) =>
			api.post(`/bookings/${bookingId}/notes`, { content: text }),
		onSuccess: () => {
			queryClient.invalidateQueries({
				queryKey: ["booking-notes", bookingId],
			});
			toast("Notiz gespeichert", "success");
			setContent("");
		},
	});

	function handleSubmit(e: React.FormEvent) {
		e.preventDefault();
		if (!content.trim()) return;
		addNote.mutate(content.trim());
	}

	const notes = query.data?.data ?? [];

	return (
		<div className="space-y-4">
			{/* New Note Form */}
			<form onSubmit={handleSubmit} className="card space-y-3">
				<textarea
					value={content}
					onChange={(e) => setContent(e.target.value)}
					placeholder="Neue Notiz schreiben..."
					className="input min-h-[80px]"
				/>
				<div className="flex justify-end">
					<button
						type="submit"
						disabled={addNote.isPending || !content.trim()}
						className="btn-primary text-sm disabled:opacity-40"
					>
						{addNote.isPending ? (
							<Loader2 size={14} className="animate-spin inline mr-1" />
						) : (
							<Send size={14} className="inline mr-1" />
						)}
						Notiz hinzufügen
					</button>
				</div>
				{addNote.isError && (
					<p className="text-xs text-red-600">
						{addNote.error instanceof Error
							? addNote.error.message
							: "Fehler beim Speichern"}
					</p>
				)}
			</form>

			{/* Notes List */}
			{query.isLoading && (
				<div className="flex justify-center py-8">
					<Loader2 className="h-6 w-6 animate-spin text-stone-400" />
				</div>
			)}

			{notes.length === 0 && !query.isLoading && (
				<div className="text-center py-12 text-stone-400">
					Noch keine Notizen.
				</div>
			)}

			<div className="space-y-2">
				{notes.map((note) => (
					<div key={note.id} className="card flex items-start gap-3 !py-3">
						<MessageSquare
							size={14}
							className="text-stone-400 mt-0.5 shrink-0"
						/>
						<div className="flex-1 min-w-0">
							<p className="text-sm text-stone-800 whitespace-pre-wrap">
								{note.newValue}
							</p>
							<p className="text-xs text-stone-400 mt-1">
								{relativeTime(note.createdAt)} · {note.source}
							</p>
						</div>
					</div>
				))}
			</div>
		</div>
	);
}
